import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronRight, Eye, Info, Landmark, Shield, X } from "lucide-react";
import DatePickerField from "../../components/common/DatePickerField";
import { useToast } from "../Context/ToastContext";
import {
  calcPt,
  fetchPtHistory,
  fetchPtRule,
  fetchPtStates,
  formatStatutoryError,
  savePt,
} from "./statutoryApi";
import { todayISO } from "./statutoryCalculators";
import { Field, focusFirstError, Toggle } from "./statutoryFormControls";

/**
 * Professional Tax settings.
 *
 * HR configures applicability only (enabled, registration, state, cycle, effective date).
 * Slabs, rule version and calculation come from the BrixiGo statutory rule master via the API;
 * nothing here edits or stores slab values.
 */

const DEFAULT_STATE = "TG";
const HISTORY_PAGE_SIZE = 10;

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const num = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

function fmtDate(value) {
  if (!value) return "—";
  const d = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  return `${date} ${time}`;
}

function slabLabel(slab) {
  if (!slab) return "—";
  if (slab.to_salary == null) return `${inr(slab.from_salary)} and Above`;
  return `${inr(slab.from_salary)} – ${inr(slab.to_salary)}`;
}

const normalizeRegistration = (v) => String(v || "").replace(/\s+/g, "").toUpperCase();

/** Digits-only formats with a fixed lead (e.g. Andhra Pradesh: 11 digits starting with 28). */
const isNumericPrefixed = (format) => format?.allowed === "0-9" && Boolean(format?.prefix);

/** Specific message for a digits-only state format; null when the number is valid. */
function numericRegistrationError(number, format) {
  const spec = `${format.max_length} digits starting with ${format.prefix}`;
  if (/\D/.test(number)) return `Invalid PT Registration Number. Only numbers are allowed. Format: ${spec}.`;
  if (!format.prefix.startsWith(number.slice(0, format.prefix.length))) {
    return `Invalid PT Registration Number. It must start with ${format.prefix}. Format: ${spec}.`;
  }
  if (!new RegExp(format.pattern).test(number)) return `Invalid PT Registration Number. Please enter exactly ${spec}.`;
  return null;
}

/** Registration-number check against the selected state's format (from GET /statutory/pt/states). */
function registrationError(value, format, { required }) {
  const number = normalizeRegistration(value);
  const example = format?.example ? ` Example: ${format.example}` : "";
  if (!number) return required ? "PT Registration Number is required." : null;
  if (!format) return null; // format not loaded yet — the server re-validates on save
  if (isNumericPrefixed(format)) return numericRegistrationError(number, format);
  return new RegExp(format.pattern).test(number) ? null : `${format.message}${example}`;
}

/** Keep only the characters the state's format allows, capped at its length. */
function sanitizeRegistration(raw, format) {
  const upper = String(raw || "").toUpperCase().replace(/\s+/g, "");
  if (!format) return upper;
  return upper.replace(new RegExp(`[^${format.allowed}]`, "g"), "").slice(0, format.max_length);
}

/**
 * While typing: only flag a number that can no longer become valid (wrong
 * leading digits). An incomplete-but-correct number (e.g. "36") is not an
 * error yet — the length check runs on blur and Save.
 */
function liveRegistrationError(value, format) {
  const number = normalizeRegistration(value);
  const prefix = format?.prefix || "";
  if (!number || !prefix) return null;
  const head = number.slice(0, prefix.length);
  return prefix.startsWith(head) ? null : registrationError(number, format, { required: false });
}

function toForm(saved) {
  return {
    is_enabled: Boolean(saved?.is_enabled),
    registration_number: saved?.registration_number || "",
    enrollment_number: saved?.enrollment_number || "",
    state_code: (saved?.state_code || DEFAULT_STATE).toUpperCase(),
    deduction_cycle: "monthly",
    effective_from: saved?.effective_from || todayISO(),
  };
}

function SlabTable({ slabs }) {
  return (
    <div className="stat-table-wrap">
      <table className="stat-table pt-slab-table">
        <thead>
          <tr>
            <th>From (₹)</th>
            <th>To (₹)</th>
            <th>PT Amount (₹)</th>
          </tr>
        </thead>
        <tbody>
          {slabs.map((s) => (
            <tr key={`${s.from_salary}-${s.to_salary}`}>
              <td>{num(s.from_salary)}</td>
              <td>{s.to_salary == null ? "Above" : num(s.to_salary)}</td>
              <td>{num(s.pt_amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ProfessionalTaxPanel({ savedPt, fallbackRegistrationNumber = "", onSaved }) {
  const toast = useToast();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const showBreadcrumb = !pathname.startsWith("/settings/"); // SettingsLayout renders its own

  const [saved, setSaved] = useState(() => toForm(savedPt));
  const [form, setForm] = useState(() => ({
    ...toForm(savedPt),
    registration_number: savedPt?.registration_number || fallbackRegistrationNumber || "",
  }));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [states, setStates] = useState([]);
  const [rule, setRule] = useState(null);
  const [ruleError, setRuleError] = useState("");
  const [ruleLoading, setRuleLoading] = useState(false);

  const [testSalary, setTestSalary] = useState("");
  const [calc, setCalc] = useState(null);
  const [calcError, setCalcError] = useState("");
  const [calculating, setCalculating] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(0);
  const [viewRow, setViewRow] = useState(null);
  // eslint-disable-next-line no-unused-vars -- read by the history-dialog slabs (commented out below)
  const [viewRule, setViewRule] = useState(null);

  const dirty = useMemo(
    () => ["is_enabled", "registration_number", "state_code", "effective_from"].some((k) => form[k] !== saved[k]),
    [form, saved]
  );

  const stateName = useCallback(
    (code) => states.find((s) => s.code === code)?.name || rule?.state_name || code || "—",
    [states, rule]
  );

  const loadHistory = useCallback(() => {
    fetchPtHistory()
      .then((rows) => setHistory(Array.isArray(rows) ? rows : []))
      .catch(() => setHistory([]));
  }, []);

  useEffect(() => {
    fetchPtStates()
      .then((rows) => setStates(Array.isArray(rows) ? rows : []))
      .catch(() => setStates([]));
    loadHistory();
  }, [loadHistory]);

  // Statutory rule (slabs + version) for the selected state as of the Effective From date.
  useEffect(() => {
    if (!form.state_code) return undefined;
    let cancelled = false;
    setRuleLoading(true);
    setRuleError("");
    fetchPtRule(form.state_code, form.effective_from)
      .then((r) => { if (!cancelled) setRule(r); })
      .catch(() => {
        if (cancelled) return;
        setRule(null);
        setRuleError("BrixiGo does not have a statutory Professional Tax rule for this state yet.");
      })
      .finally(() => { if (!cancelled) setRuleLoading(false); });
    return () => { cancelled = true; };
  }, [form.state_code, form.effective_from]);

  const calcRequestRef = useRef(0);

  const runCalculation = useCallback(async () => {
    const requestId = ++calcRequestRef.current;
    const salary = Number(testSalary);
    if (testSalary === "" || !Number.isFinite(salary) || salary < 0) {
      setCalc(null);
      setCalcError("Enter a valid monthly salary.");
      return;
    }
    setCalculating(true);
    setCalcError("");
    try {
      const res = await calcPt({ salary, state_code: form.state_code, as_of: form.effective_from });
      if (requestId === calcRequestRef.current) setCalc(res);
    } catch (e) {
      if (requestId !== calcRequestRef.current) return;
      setCalc(null);
      setCalcError(formatStatutoryError(e, "Could not calculate PT"));
    } finally {
      if (requestId === calcRequestRef.current) setCalculating(false);
    }
  }, [testSalary, form.state_code, form.effective_from]);

  // The result is shown only after Calculate is clicked; clear it when the inputs change.
  useEffect(() => {
    calcRequestRef.current += 1; // drop any in-flight result for the old inputs
    setCalc(null);
    setCalcError("");
    setCalculating(false);
  }, [testSalary, form.state_code, form.effective_from]);

  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
  };

  const validate = () => {
    const next = {};
    if (form.is_enabled) {
      const regErr = registrationError(form.registration_number, regFormat, { required: true });
      if (regErr) next.registration_number = regErr;
      if (!form.state_code) next.state_code = "State is required when Professional Tax is enabled.";
      else if (!rule) next.state_code = "No statutory PT rule is available for this state.";
    }
    // While PT is disabled the registration field is hidden, so it is not validated.
    if (!form.effective_from) next.effective_from = "Effective From date is required.";
    return next;
  };

  const handleSave = async () => {
    const next = validate();
    setErrors(next);
    if (Object.keys(next).some((k) => next[k])) {
      focusFirstError(next);
      toast.error("Fix the highlighted fields before saving.");
      return;
    }
    const payload = {
      is_enabled: form.is_enabled,
      registration_number: normalizeRegistration(form.registration_number) || null,
      enrollment_number: form.enrollment_number || null,
      state_code: form.state_code,
      deduction_cycle: "monthly",
      effective_from: form.effective_from,
    };
    try {
      setSaving(true);
      const row = await savePt(payload);
      const savedForm = toForm(row || payload);
      setSaved(savedForm);
      setForm(savedForm);
      onSaved?.(row || payload);
      loadHistory();
      toast.success(`Professional Tax settings saved — effective from ${fmtDate(savedForm.effective_from)}.`);
    } catch (e) {
      toast.error(formatStatutoryError(e, "Could not save Professional Tax settings"));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setForm(saved);
    setErrors({});
  };

  const openHistoryRow = (row) => {
    setViewRow(row);
    setViewRule(null);
    // Slabs are not shown in the history dialog right now, so the rule is not fetched.
    // if (row.state_code) {
    //   fetchPtRule(row.state_code, row.effective_from).then(setViewRule).catch(() => setViewRule(null));
    // }
  };

  const stateOptions = useMemo(() => {
    const list = [...states];
    if (form.state_code && !list.some((s) => s.code === form.state_code)) {
      list.push({ code: form.state_code, name: form.state_code }); // keep a legacy saved state visible
    }
    return list;
  }, [states, form.state_code]);

  const regFormat = states.find((st) => st.code === form.state_code)?.registration || null;

  // Selected state drives the registration format — re-check an entered number when it changes.
  useEffect(() => {
    if (!regFormat || !normalizeRegistration(form.registration_number)) return;
    const err = registrationError(form.registration_number, regFormat, { required: false });
    setErrors((prev) => (prev.registration_number === (err || undefined) ? prev : { ...prev, registration_number: err || undefined }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regFormat]);

  // Close the history dialog with Escape and stop the page scrolling behind it.
  useEffect(() => {
    if (!viewRow) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setViewRow(null); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [viewRow]);

  const historyPages = Math.max(1, Math.ceil(history.length / HISTORY_PAGE_SIZE));
  const historySlice = history.slice(historyPage * HISTORY_PAGE_SIZE, (historyPage + 1) * HISTORY_PAGE_SIZE);
  const selectedStateName = stateName(form.state_code);

  return (
    <div className="pt-page">
      {showBreadcrumb && (
        <div className="sw-breadcrumbs">
          <button type="button" className="sw-breadcrumb-link" onClick={() => navigate("/statutory/pf")}>
            Statutory
          </button>
          <ChevronRight size={12} className="sw-breadcrumb-sep" />
          <span className="sw-breadcrumb-current">PT Settings</span>
        </div>
      )}

      <div className="sw-page-hero pt-hero">
        <div className="sw-page-hero-icon"><Shield size={22} /></div>
        <div className="sw-page-hero-text">
          <h1 className="sw-page-hero-title">Professional Tax (PT)</h1>
          <p className="sw-page-hero-sub">Configure Professional Tax settings for your organisation.</p>
        </div>
        <div className="pt-hero-meta">
          <span className={`pt-status-badge ${saved.is_enabled ? "on" : "off"}`}>
            {saved.is_enabled ? "Active" : "Inactive"}
          </span>
          {saved.is_enabled && (
            <span className="pt-hero-applies">
              <Landmark size={14} /> Applies to employees in {stateName(saved.state_code)}
            </span>
          )}
        </div>
      </div>

      <div className={`pt-grid${form.is_enabled ? "" : " pt-grid--off"}`}>
        {/* ── Professional Tax Settings ── */}
        <section className="stat-panel pt-card">
          <h2>Professional Tax Settings</h2>
          <div className="pt-toggle-wrap" title="When disabled, PT is not deducted during payroll calculation.">
            <Toggle checked={form.is_enabled} onChange={(v) => update("is_enabled", v)} label="Enable PT" />
            <span className="pt-toggle-state">{form.is_enabled ? "Yes" : "No"}</span>
          </div>
          {/* Like EPF / ESI: configuration fields appear only once PT is enabled. */}
          {!form.is_enabled ? (
            <>
              <p className="pt-muted pt-off-note">
                Professional Tax is disabled, so PT is not deducted during payroll. Turn on <strong>Enable PT</strong> to
                configure the registration number, state and effective date.
              </p>
              <div className="pt-actions pt-actions--inline">
                <button type="button" className="stat-btn stat-btn-ghost" onClick={handleReset} disabled={saving || !dirty}>
                  Reset
                </button>
                <button type="button" className="stat-btn stat-btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? "Saving…" : "Save Settings"}
                </button>
              </div>
            </>
          ) : (
          <div className="stat-grid-2">
            <Field
              label="Registration Number"
              required={form.is_enabled}
              fieldKey="registration_number"
              error={errors.registration_number}
              hint={regFormat?.hint}
            >
              <input
                className={errors.registration_number ? "stat-input-err" : ""}
                value={form.registration_number}
                placeholder={regFormat?.example ? `e.g. ${regFormat.example}` : "As printed on your PT certificate"}
                autoComplete="off"
                inputMode={regFormat?.allowed?.startsWith("0-9") ? "numeric" : "text"}
                maxLength={regFormat ? undefined : 20}
                onChange={(e) => {
                  // No maxLength while a format is known: sanitizeRegistration caps the length after
                  // stripping, so a pasted value keeps its digits and a typed letter is still reported.
                  const el = e.target;
                  const raw = el.value;
                  const caret = el.selectionStart ?? raw.length;
                  const next = sanitizeRegistration(raw, regFormat);
                  // Longer than the state allows (e.g. a pasted value) is reported, not just cut off.
                  const tooLong = Boolean(regFormat)
                    && raw.toUpperCase().replace(new RegExp(`[^${regFormat.allowed}]`, "g"), "").length
                      > regFormat.max_length;
                  const rejected = regFormat
                    ? new RegExp(`[^${regFormat.allowed}]`).test(raw.toUpperCase()) || tooLong
                    : /\s/.test(raw);
                  update("registration_number", next);
                  const liveErr = rejected
                    ? (isNumericPrefixed(regFormat)
                      ? numericRegistrationError(raw.toUpperCase(), regFormat)
                      : regFormat?.no_spaces
                        // Spaces/special characters are invalid as typed, not silently removed.
                        ? regFormat.message
                        : registrationError(raw, regFormat, { required: false }))
                    : next
                      ? liveRegistrationError(next, regFormat)
                      : registrationError(next, regFormat, { required: form.is_enabled });
                  if (liveErr) setErrors((prev) => ({ ...prev, registration_number: liveErr }));
                  // Keep the caret in place when a stripped character leaves the value unchanged.
                  const pos = Math.min(sanitizeRegistration(raw.slice(0, caret), regFormat).length, next.length);
                  queueMicrotask(() => {
                    if (document.activeElement === el) el.setSelectionRange(pos, pos);
                  });
                }}
                onBlur={() => {
                  const err = registrationError(form.registration_number, regFormat, { required: form.is_enabled });
                  if (err) setErrors((prev) => ({ ...prev, registration_number: err }));
                  else update("registration_number", normalizeRegistration(form.registration_number));
                }}
              />
            </Field>
            <Field label="State" required={form.is_enabled} fieldKey="state_code" error={errors.state_code}>
              <select
                className={errors.state_code ? "stat-input-err" : ""}
                value={form.state_code}
                onChange={(e) => update("state_code", e.target.value)}
              >
                {stateOptions.map((s) => (
                  <option key={s.code} value={s.code}>{s.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Deduction Cycle" hint="PT is deducted every month through payroll.">
              <select value="monthly" onChange={() => {}}>
                <option value="monthly">Monthly</option>
              </select>
            </Field>
            <Field
              label="Effective From"
              required
              fieldKey="effective_from"
              error={errors.effective_from}
              hint="A new date adds a new version; earlier versions are kept in history."
            >
              <DatePickerField
                hideLabel
                value={form.effective_from}
                onChange={(date) => update("effective_from", date)}
                placeholder="Effective from"
              />
            </Field>
          </div>
          )}
        </section>

        {form.is_enabled && (
        <>
        {/* ── Read-only statutory slabs ── */}
        <section className="stat-panel pt-card">
          <div className="pt-card-head">
            <h2>{selectedStateName} Professional Tax Slabs</h2>
            <span className="pt-system-badge">System Managed</span>
          </div>
          <p className="sub">
            BrixiGo applies the applicable {selectedStateName} Professional Tax rules based on the selected state and
            effective date. Statutory slab values are system managed.
          </p>
          {ruleLoading && !rule ? (
            <p className="pt-muted">Loading statutory rates…</p>
          ) : ruleError ? (
            <div className="stat-alert warn">{ruleError}</div>
          ) : rule ? (
            <>
              <SlabTable slabs={rule.slabs} />
              <div className="stat-alert info pt-info">
                <Info size={15} />
                <span>
                  These are the current statutory rates for {selectedStateName} (rule {rule.rule_version}). They will be
                  applied automatically during payroll calculation.
                </span>
              </div>
            </>
          ) : null}
        </section>

        {/* ── PT Calculation Test (preview only) ── */}
        <section className="stat-panel pt-card">
          <h2>PT Calculation Test</h2>
          <p className="sub">Preview only — nothing is saved and no payroll is changed.</p>
          <div className="pt-calc">
            <div className="pt-calc-input">
              <Field label="Test Salary" hint="Monthly gross salary">
                <input
                  type="number"
                  min="0"
                  inputMode="numeric"
                  value={testSalary}
                  placeholder="e.g. 25000"
                  onChange={(e) => setTestSalary(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") runCalculation(); }}
                />
              </Field>
              <button
                type="button"
                className="stat-btn stat-btn-primary"
                onClick={runCalculation}
                disabled={calculating || !rule}
              >
                {calculating ? "Calculating…" : "Calculate"}
              </button>
            </div>
            <div className="pt-calc-result" aria-live="polite">
              {calcError ? (
                <p className="stat-field-error">{calcError}</p>
              ) : !calc ? (
                <p className="pt-muted pt-calc-empty">
                  {calculating ? "Calculating…" : "Enter a monthly salary and click Calculate to see the PT amount."}
                </p>
              ) : (
                <>
                  <span className="pt-calc-label">Professional Tax</span>
                  <strong className="pt-calc-amount">{calc ? inr(calc.pt_deduction) : "—"}</strong>
                  <span className="pt-calc-label">Applicable Slab</span>
                  <strong className="pt-calc-slab">{calc ? slabLabel(calc.applicable_slab) : "—"}</strong>
                </>
              )}
            </div>
          </div>
        </section>

        {/* ── Important notes + actions ── */}
        <div className="pt-notes-col">
          <section className="pt-notes">
            <h3><Info size={16} /> Important Notes</h3>
            <ul>
              <li>Professional Tax calculation is based on the applicable state statutory rules.</li>
              <li>Slab values and calculation logic are system managed.</li>
              <li>HR/Organisation users cannot modify statutory slab values.</li>
              <li>Use the PT Calculation Test to preview the applicable PT amount.</li>
              <li>Changes to PT settings will be effective from the selected date.</li>
            </ul>
          </section>
          <div className="pt-actions">
            <button type="button" className="stat-btn stat-btn-ghost" onClick={handleReset} disabled={saving || !dirty}>
              Reset
            </button>
            <button type="button" className="stat-btn stat-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save Settings"}
            </button>
          </div>
        </div>
        </>
        )}
      </div>

      {/* ── History ── */}
      <section className="stat-panel pt-history">
        <h2>PT Settings History</h2>
        <div className="stat-table-wrap">
          <table className="stat-table pt-history-table">
            <thead>
              <tr>
                <th>Effective From</th>
                <th>State</th>
                <th>PT Enabled</th>
                <th>Deduction Cycle</th>
                <th>Rule Version</th>
                <th>Updated By</th>
                <th>Updated On</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {historySlice.length === 0 ? (
                <tr>
                  <td colSpan={8} className="pt-muted pt-empty">No PT settings saved yet.</td>
                </tr>
              ) : (
                historySlice.map((row) => (
                  <tr key={row.id}>
                    <td>{fmtDate(row.effective_from)}</td>
                    <td>{row.state_name || row.state_code || "—"}</td>
                    <td>{row.is_enabled ? "Yes" : "No"}</td>
                    <td className="pt-cap">{row.deduction_cycle || "monthly"}</td>
                    <td>{row.rule_version || "—"}</td>
                    <td>{row.updated_by || "—"}</td>
                    <td>{fmtDateTime(row.updated_on)}</td>
                    <td>
                      <button
                        type="button"
                        className="pt-icon-btn"
                        onClick={() => openHistoryRow(row)}
                        aria-label={`View PT settings effective ${fmtDate(row.effective_from)}`}
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="pt-history-foot">
          <span>
            Showing {history.length ? historyPage * HISTORY_PAGE_SIZE + 1 : 0} – {historyPage * HISTORY_PAGE_SIZE + historySlice.length} of{" "}
            {history.length} records
          </span>
          {historyPages > 1 && (
            <span className="pt-pager">
              <button type="button" className="stat-btn stat-btn-ghost" disabled={historyPage === 0} onClick={() => setHistoryPage((p) => p - 1)}>
                Previous
              </button>
              <button
                type="button"
                className="stat-btn stat-btn-ghost"
                disabled={historyPage >= historyPages - 1}
                onClick={() => setHistoryPage((p) => p + 1)}
              >
                Next
              </button>
            </span>
          )}
        </div>
      </section>

      {/* Portal to <body> so the dialog opens centred in the viewport, not at the top of a transformed page. */}
      {viewRow && createPortal(
        <div className="pt-modal-overlay" role="presentation" onClick={() => setViewRow(null)}>
          <div className="pt-modal" role="dialog" aria-modal="true" aria-label="PT settings version" onClick={(e) => e.stopPropagation()}>
            <div className="pt-modal-head">
              <h3>PT Settings — effective {fmtDate(viewRow.effective_from)}</h3>
              <button type="button" className="pt-icon-btn" onClick={() => setViewRow(null)} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <dl className="pt-view-grid">
              <dt>State</dt><dd>{viewRow.state_name || viewRow.state_code || "—"}</dd>
              <dt>PT Enabled</dt><dd>{viewRow.is_enabled ? "Yes" : "No"}</dd>
              <dt>Registration Number</dt><dd>{viewRow.registration_number || "—"}</dd>
              <dt>Deduction Cycle</dt><dd className="pt-cap">{viewRow.deduction_cycle || "monthly"}</dd>
              <dt>Rule Version</dt><dd>{viewRow.rule_version || "—"}</dd>
              <dt>Updated By</dt><dd>{viewRow.updated_by || "—"}</dd>
              <dt>Updated On</dt><dd>{fmtDateTime(viewRow.updated_on)}</dd>
            </dl>
            {/* Statutory slabs hidden in the history dialog for now (re-enable the fetch in openHistoryRow too).
            {viewRule && (
              <>
                <h4 className="pt-view-sub">Statutory slabs ({viewRule.rule_version}) — read only</h4>
                <SlabTable slabs={viewRule.slabs} />
              </>
            )}
            */}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
